/**
 * "Choose guide" search (Phase 3 step 4, docs/build-guide-playlists-epg.md §5):
 * the guide channels of a playlist's own guide and its switched-on extra
 * sources, found by the words of their names. Plain rules, kept apart from the
 * dialog so they can be tested.
 */

/** One guide channel the viewer can pin. */
export interface GuideChoice {
  /** "own", or the extra source's id (GuideFeedConfig.id). */
  feed: string;
  /** The source's name on screen (its host). */
  feedLabel: string;
  /** The source's address, to ask what is on now. Never shown. */
  url: string;
  guideChannelId: string;
  name: string;
  /** The name's and id's words, worked out once. */
  words: string[];
}

/**
 * Words that say nothing about which channel it is: quality, and country codes,
 * which most guide channel ids end in ("CNN.us"), so "us" matched almost every
 * one (onn box, 2026-10-23: "max us 03" listed NHL, TMZ, Vice...).
 */
const NEUTRAL = new Set(["hd", "fhd", "uhd", "sd", "4k", "hevc", "h265", "raw", "live", "backup", "us", "usa", "uk", "gb", "ca", "au"]);

/** A name's words, lower-cased; quality and country words left out. "USA: ESPN 2 HD" → ["espn", "2"]. */
export function searchWords(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^\p{L}\p{N}+]+/u)
    .filter((word) => word.length > 0 && !NEUTRAL.has(word));
}

/**
 * What the search starts from: the channel's name without a country or
 * provider tag in front ("US: ", "UK | ", "[CA] ") and without quality words.
 */
export function suggestedQuery(channelName: string): string {
  const bare = channelName
    .replace(/^\s*[[(]?[A-Za-z]{2,4}[\])]?\s*[:|\-–]\s*/, "")
    .replace(/^\s*\[[^\]]{1,6}\]\s*/, "");
  return searchWords(bare).join(" ");
}

export function choiceOf(feed: string, feedLabel: string, url: string, guideChannelId: string, displayName: string | undefined): GuideChoice {
  const name = displayName?.trim() || guideChannelId;
  return { feed, feedLabel, url, guideChannelId, name, words: [...new Set([...searchWords(name), ...searchWords(guideChannelId)])] };
}

/**
 * The choices matching a search, best first: those with every word of the
 * search, then those with most of them (a word may also match the start of one
 * of theirs, counting less); shorter names first among equals, so "ESPN" comes
 * before "ESPN Deportes".
 * Nothing for an empty search.
 */
export function rankChoices(choices: readonly GuideChoice[], query: string, limit = 40): GuideChoice[] {
  const wanted = searchWords(query);
  if (wanted.length === 0) return [];
  const scored: Array<{ choice: GuideChoice; score: number }> = [];
  for (const choice of choices) {
    let score = 0;
    for (const word of wanted) {
      if (choice.words.includes(word)) score += 2;
      else if (choice.words.some((own) => own.startsWith(word))) score += 1;
    }
    if (score > 0) scored.push({ choice, score });
  }
  const full = wanted.length * 2;
  scored.sort((a, b) => {
    const aAll = a.score >= full ? 1 : 0;
    const bAll = b.score >= full ? 1 : 0;
    return bAll - aAll || b.score - a.score || a.choice.name.length - b.choice.name.length || a.choice.name.localeCompare(b.choice.name);
  });
  return scored.slice(0, limit).map((entry) => entry.choice);
}
